/**
 * Quest chain — guided early progression with automatic rewards.
 * Completing a mission GIVES the item (e.g. a bed) instead of forcing
 * Minecraft-style grinding; more motivating for the 2V community MVP.
 */

import { BlockType } from "../world/block-registry";
import { ItemType } from "./items";

export type QuestEventType = "break" | "place" | "craft" | "kill" | "eat";

export type QuestGoal = {
  type: QuestEventType;
  /** Restrict to a specific block/item id (undefined = any). */
  targetId?: number;
  count: number;
};

export type QuestReward = {
  items: Array<{ id: number; count: number }>;
  xp: number;
};

export type QuestDef = {
  id: string;
  title: string;
  description: string;
  goal: QuestGoal;
  reward: QuestReward;
  rewardLabel: string;
};

export const QUEST_CHAIN: QuestDef[] = [
  {
    id: "lumberjack",
    title: "Lenhador",
    description: "Quebre 5 blocos de madeira",
    goal: { type: "break", targetId: BlockType.WOOD, count: 5 },
    reward: { items: [{ id: BlockType.CRAFTING_TABLE, count: 1 }, { id: BlockType.PLANKS, count: 8 }], xp: 20 },
    rewardLabel: "Mesa de trabalho + 8 tábuas",
  },
  {
    id: "artisan",
    title: "Artesão",
    description: "Crafte 4 gravetos (perto da mesa de trabalho)",
    goal: { type: "craft", targetId: ItemType.STICK, count: 4 },
    reward: { items: [{ id: ItemType.WOODEN_PICKAXE, count: 1 }], xp: 25 },
    rewardLabel: "Picareta de madeira",
  },
  {
    id: "miner",
    title: "Minerador",
    description: "Minere 10 pedras",
    goal: { type: "break", targetId: BlockType.STONE, count: 10 },
    reward: {
      items: [
        { id: ItemType.WOODEN_AXE, count: 1 },
        { id: ItemType.WOODEN_SHOVEL, count: 1 },
      ],
      xp: 30,
    },
    rewardLabel: "Machado + pá de madeira",
  },
  {
    id: "hunter",
    title: "Caçador",
    description: "Derrote 2 monstros",
    goal: { type: "kill", count: 2 },
    reward: { items: [{ id: ItemType.STONE_SWORD, count: 1 }], xp: 40 },
    rewardLabel: "Espada de pedra",
  },
  {
    id: "farmer",
    title: "Fazendeiro",
    description: "Coma 2 alimentos",
    goal: { type: "eat", count: 2 },
    reward: { items: [{ id: BlockType.BED, count: 1 }], xp: 40 },
    rewardLabel: "Cama (define seu respawn e pula a noite)",
  },
  {
    id: "builder",
    title: "Construtor",
    description: "Coloque 20 blocos no mundo",
    goal: { type: "place", count: 20 },
    reward: { items: [{ id: BlockType.GLASS, count: 16 }], xp: 60 },
    rewardLabel: "16 vidros + bônus de XP",
  },
];

export type QuestState = {
  /** Index into QUEST_CHAIN of the active quest (== length when done). */
  activeIndex: number;
  progress: number;
};

export function createQuestState(): QuestState {
  return { activeIndex: 0, progress: 0 };
}

export function getActiveQuest(state: QuestState): QuestDef | null {
  return QUEST_CHAIN[state.activeIndex] ?? null;
}

/**
 * Feed a gameplay event into the quest system.
 * Returns the quest just completed (reward must be granted by the caller),
 * or null if nothing completed.
 */
export function questEvent(
  state: QuestState,
  type: QuestEventType,
  targetId?: number,
  amount = 1
): QuestDef | null {
  const quest = getActiveQuest(state);
  if (!quest) return null;
  if (quest.goal.type !== type) return null;
  if (quest.goal.targetId !== undefined && quest.goal.targetId !== targetId) return null;

  state.progress += amount;
  if (state.progress >= quest.goal.count) {
    state.activeIndex++;
    state.progress = 0;
    return quest;
  }
  return null;
}

export function serializeQuestState(state: QuestState): string {
  return JSON.stringify(state);
}

export function deserializeQuestState(raw: string | null): QuestState {
  if (!raw) return createQuestState();
  try {
    const parsed = JSON.parse(raw) as QuestState;
    if (
      typeof parsed?.activeIndex === "number" &&
      typeof parsed?.progress === "number" &&
      parsed.activeIndex >= 0 &&
      parsed.activeIndex <= QUEST_CHAIN.length
    ) {
      return { activeIndex: parsed.activeIndex, progress: Math.max(0, parsed.progress) };
    }
  } catch {
    // fall through to fresh state
  }
  return createQuestState();
}
